// frontend-web/src/pages/Admin/AdminUsers.jsx
import React, { useEffect, useState } from "react";
import api, { getCurrentUser, hasRole } from "../../api";
import { useNavigate } from "react-router-dom";

export default function AdminUsers() {
  const nav = useNavigate();

  // State
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 12;

  // Modals / Forms
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "user", clazz: ""
  });

  // UX helpers
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/users/list");
      const list = res.data.users || [];
      setUsers(list);
      setFiltered(list);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed fetching users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Search + role filter
  useEffect(() => {
    let out = users.slice();

    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      out = out.filter(u =>
        (u.name || "").toLowerCase().includes(ql) ||
        (u.email || "").toLowerCase().includes(ql) ||
        (u.phone || "").toLowerCase().includes(ql)
      );
    }

    if (roleFilter) {
      out = out.filter(u => u.role === roleFilter);
    }

    setFiltered(out);
    setPage(1);
  }, [q, roleFilter, users]);

  // Pagination helpers
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  // Form handlers
  const handleFormChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Create user
  const createUser = async () => {
    if (!form.name) return alert("Name required");
    try {
      const payload = { ...form, password: "changeme123" }; // temp pass
      const res = await api.post("/admin/users", payload);
      alert("User created");
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", role: "user", clazz: "" });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Create failed");
    }
  };

  // Edit user open
  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      role: u.role || "user",
      clazz: u.clazz || ""
    });
    setShowEdit(true);
  };

  // Save edit
  const saveEdit = async () => {
    if (!editUser) return;
    try {
      await api.put(`/admin/users/${editUser.id}`, form);
      alert("Saved");
      setShowEdit(false);
      setEditUser(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Update failed");
    }
  };

  // Delete user
  const deleteUser = async (id) => {
    if (!window.confirm("Delete user? This is irreversible.")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      alert("Deleted");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  // Deactivate / Activate
  const toggleActive = async (id, current) => {
    try {
      const action = current ? "deactivate" : "activate";
      await api.post(`/admin/users/${id}/${action}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    }
  };

  return (
    <div className="p-6" style={{ fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>User Management</h2>
          <p style={{ color: "#6b7280" }}>View, add, edit, deactivate users</p>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => nav("/admin")} style={btnOutline}>⟵ Back to Dashboard</button>
          <button onClick={() => { setShowAdd(true); }} style={btnPrimary}>+ Add User</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ marginTop: 18, display: "flex", gap: 12, alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / email / phone" style={searchStyle} />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={selectStyle}>
          <option value="">All roles</option>
          <option value="super_admin">super_admin</option>
          <option value="management">management</option>
          <option value="admin">admin</option>
          <option value="user">user</option>
        </select>
        <button onClick={() => { setQ(""); setRoleFilter(""); }} style={btnGhost}>Clear</button>
      </div>

      {/* Table */}
      <div style={{ marginTop: 18, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f3f4f6" }}>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Email</th>
              <th style={th}>Phone</th>
              <th style={th}>Role</th>
              <th style={th}>Plan</th>
              <th style={th}>Status</th>
              <th style={th}>Subjects</th>
              <th style={th}>Start–End</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} style={{ padding: 20, textAlign: "center" }}>Loading...</td></tr>
            )}

            {!loading && pageItems.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 20, textAlign: "center" }}>No users found</td></tr>
            )}

            {!loading && pageItems.map(u => (
              <tr key={u.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                <td style={td}>{u.name}</td>
                <td style={td}>{u.email || "-"}</td>
                <td style={td}>{u.phone || "-"}</td>
                <td style={td}>{u.role}</td>
                <td style={td}>{u.subscription?.plan_name || "No Plan"}</td>
                <td style={td}>{u.subscription?.status || "inactive"}</td>
                <td style={td}>{(u.subscription?.subjects || []).map(s => s.name).join(", ") || "-"}</td>
                <td style={td}>{u.subscription?.starts_at ? `${u.subscription.starts_at.split("T")[0]} → ${u.subscription.ends_at?.split("T")[0] || "-"}` : "-"}</td>
                <td style={td}>
                  <button onClick={() => nav(`/admin/user/${u.id}`)}>View</button>
                  <button onClick={() => openEdit(u)} style={miniBtn}>Edit</button>
                  <button onClick={() => deleteUser(u.id)} style={{ ...miniBtn, background: "#ef4444" }}>Delete</button>
                  <button onClick={() => toggleActive(u.id, (u.subscription?.status === 'paid'))} style={{ ...miniBtn, background: "#64748b" }}>
                    {u.subscription?.status === 'paid' ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} style={pageBtn} disabled={page === 1}>Prev</button>
        <div>Page {page} / {totalPages}</div>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={pageBtn} disabled={page === totalPages}>Next</button>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <Modal title="Add User" onClose={() => setShowAdd(false)}>
          <label style={label}>Name</label>
          <input style={input} value={form.name} onChange={e => handleFormChange("name", e.target.value)} />
          <label style={label}>Email</label>
          <input style={input} value={form.email} onChange={e => handleFormChange("email", e.target.value)} />
          <label style={label}>Phone</label>
          <input style={input} value={form.phone} onChange={e => handleFormChange("phone", e.target.value)} />
          <label style={label}>Role</label>
          <select style={input} value={form.role} onChange={e => handleFormChange("role", e.target.value)}>
            <option value="user">user</option>
            <option value="admin">admin</option>
            <option value="management">management</option>
            <option value="super_admin">super_admin</option>
          </select>
          <label style={label}>Class (id)</label>
          <input style={input} value={form.clazz} onChange={e => handleFormChange("clazz", e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={createUser} style={btnPrimary}>Create</button>
            <button onClick={() => setShowAdd(false)} style={btnOutline}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <Modal title="Edit User" onClose={() => setShowEdit(false)}>
          <label style={label}>Name</label>
          <input style={input} value={form.name} onChange={e => handleFormChange("name", e.target.value)} />
          <label style={label}>Email</label>
          <input style={input} value={form.email} onChange={e => handleFormChange("email", e.target.value)} />
          <label style={label}>Phone</label>
          <input style={input} value={form.phone} onChange={e => handleFormChange("phone", e.target.value)} />
          <label style={label}>Role</label>
          <select style={input} value={form.role} onChange={e => handleFormChange("role", e.target.value)}>
            <option value="user">user</option>
            <option value="admin">admin</option>
            <option value="management">management</option>
            <option value="super_admin">super_admin</option>
          </select>
          <label style={label}>Class (id)</label>
          <input style={input} value={form.clazz} onChange={e => handleFormChange("clazz", e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={saveEdit} style={btnPrimary}>Save</button>
            <button onClick={() => setShowEdit(false)} style={btnOutline}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* Small presentational helpers */
const Modal = ({ title, children, onClose }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 2000
  }}>
    <div style={{ width: "96%", maxWidth: 680, background: "white", borderRadius: 10, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3>{title}</h3>
        <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }}>✕</button>
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  </div>
);

const btnPrimary = { padding: "8px 14px", background: "#2563eb", color: "white", borderRadius: 8, border: "none", cursor: "pointer" };
const btnOutline = { padding: "8px 14px", background: "white", color: "#374151", borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer" };
const btnGhost = { padding: "8px 12px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer" };
const searchStyle = { padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", width: 360 };
const selectStyle = { padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" };
const th = { textAlign: "left", padding: 10, fontWeight: 700 };
const td = { padding: 10, verticalAlign: "top" };
const miniBtn = { padding: "6px 8px", marginRight: 6, background: "#f3f4f6", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer" };
const pageBtn = { padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb", background: "white" };
const input = { padding: 10, borderRadius: 6, border: "1px solid #e5e7eb", width: "100%" };
const label = { display: "block", marginTop: 8, marginBottom: 6, fontSize: 13, color: "#374151" };
